import axios from 'axios'
import { toast } from 'react-toastify'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

// Security: Sanitize error messages before displaying to users
const sanitizeErrorMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return 'An error occurred. Please try again.'
  }

  // Remove potential HTML/script tags
  let sanitized = message.replace(/<[^>]*>/g, '')

  // Remove common sensitive patterns (file paths, stack traces, SQL)
  sanitized = sanitized
    .replace(/\/[a-zA-Z0-9_\-./]+\.(py|js|jsx|ts|tsx|json)/gi, '[path]')
    .replace(/at\s+[\w.]+\s+\([^)]+\)/g, '')
    .replace(/File\s+"[^"]+"/g, '')
    .replace(/line\s+\d+/gi, '')
    .replace(/SELECT|INSERT|UPDATE|DELETE|FROM|WHERE/gi, '[query]')

  // Truncate overly long messages
  if (sanitized.length > 200) {
    sanitized = sanitized.substring(0, 200) + '...'
  }

  return sanitized.trim() || 'An error occurred. Please try again.'
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
})

// Event listener for auth events (used by AuthContext)
let authEventCallback = null

export const setAuthEventCallback = (callback) => {
  authEventCallback = callback
}

// Token refresh queue - prevents race conditions when multiple requests fail simultaneously
let isRefreshing = false
let refreshSubscribers = []

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback)
}

const onRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

const onRefreshFailed = (error) => {
  refreshSubscribers.forEach((callback) => callback(null, error))
  refreshSubscribers = []
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh and errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Handle network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please check your connection and try again.')
      } else {
        toast.error('Network error. Please check your internet connection.')
      }
      return Promise.reject({
        ...error,
        userMessage: 'Network error. Please check your internet connection.',
        isNetworkError: true,
      })
    }

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401) {
      // If this is already a retry, force logout
      if (originalRequest._retry) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        localStorage.removeItem('permissions')

        // Notify AuthContext
        if (authEventCallback) {
          authEventCallback('logout', 'Your session has expired. Please login again.')
        }

        toast.error('Your session has expired. Please login again.')

        // Store current page to redirect back after login
        const currentPath = window.location.pathname + window.location.search
        if (currentPath !== '/login' && currentPath !== '/') {
          window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
        } else {
          window.location.href = '/login'
        }

        return Promise.reject({
          ...error,
          userMessage: 'Your session has expired. Please login again.',
          requiresLogin: true,
        })
      }

      originalRequest._retry = true

      // If refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token, refreshError) => {
            if (refreshError) {
              reject(refreshError)
            } else if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(api(originalRequest))
            } else {
              reject(error)
            }
          })
        })
      }

      isRefreshing = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) {
          throw new Error('No refresh token')
        }

        const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
          refresh: refreshToken,
        })

        const { access, refresh: newRefresh } = response.data
        localStorage.setItem('accessToken', access)
        if (newRefresh) {
          localStorage.setItem('refreshToken', newRefresh)
        }

        isRefreshing = false
        onRefreshed(access)

        originalRequest.headers.Authorization = `Bearer ${access}`
        return api(originalRequest)
      } catch (refreshError) {
        isRefreshing = false
        onRefreshFailed(refreshError)

        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        localStorage.removeItem('permissions')

        // Notify AuthContext
        if (authEventCallback) {
          authEventCallback('logout', 'Your session has expired. Please login again.')
        }

        toast.error('Your session has expired. Please login again.')

        // Store current page to redirect back after login
        const currentPath = window.location.pathname + window.location.search
        if (currentPath !== '/login' && currentPath !== '/') {
          window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
        } else {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    // Handle 403 Forbidden - Permission denied
    if (error.response?.status === 403) {
      const rawMessage = error.response?.data?.detail ||
        error.response?.data?.error ||
        'You do not have permission to perform this action. Please contact your system administrator to request access.'
      const message = sanitizeErrorMessage(rawMessage)

      toast.error(message, {
        autoClose: 5000,
      })

      return Promise.reject({
        ...error,
        userMessage: message,
        isPermissionError: true,
      })
    }

    // Handle other errors
    let userMessage = 'Something went wrong. Please try again.'
    if (error.response?.data?.detail) {
      userMessage = sanitizeErrorMessage(error.response.data.detail)
    } else if (error.response?.data?.error) {
      userMessage = sanitizeErrorMessage(error.response.data.error)
    } else if (error.response?.data?.message) {
      userMessage = sanitizeErrorMessage(error.response.data.message)
    }

    return Promise.reject({
      ...error,
      userMessage,
    })
  }
)

export default api

// Helper functions for common API operations
export const apiGet = (url, params = {}) => api.get(url, { params })
export const apiPost = (url, data = {}) => api.post(url, data)
export const apiPut = (url, data = {}) => api.put(url, data)
export const apiPatch = (url, data = {}) => api.patch(url, data)
export const apiDelete = (url) => api.delete(url)
